import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  buildInvitationPayload,
  buildRecoveryPayload,
  canInvite,
  ROLE_OPTIONS,
} from "../../invite-permissions.js";
import { findUniqueProjectByCode } from "../../project-context.js";
import { AuthLayout } from "../components/AuthLayout";
import { adminApi } from "../lib/adminApi";
import { api } from "../lib/api";
import type { AccessProject } from "../types/api";

const PROJECT_CODE = "ams11";

export function InvitePage() {
  const navigate = useNavigate();
  const [project, setProject] = useState<AccessProject | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState(ROLE_OPTIONS[0]?.code ?? "");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<
    {
      kind: "success" | "error";
      message: string;
    } | null
  >(null);

  useEffect(() => {
    let active = true;
    void api.getAccessContext().then((context) => {
      if (!active) return;
      const projectContext = findUniqueProjectByCode(context, PROJECT_CODE);
      setProject(projectContext);
      setAllowed(
        Boolean(
          projectContext?.project_id &&
            canInvite(context, projectContext.project_id),
        ),
      );
    }).catch((error) => {
      console.error("Fout bij laden uitnodigingspagina", error);
      if (active) setAllowed(false);
    });
    return () => {
      active = false;
    };
  }, []);

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);
    const result = buildInvitationPayload({
      email,
      displayName,
      roleBundleCode: role,
      project: project ?? undefined,
    });
    if ("error" in result) {
      setStatus({ kind: "error", message: result.error });
      return;
    }

    setBusy(true);
    try {
      await adminApi.sendInvitation(result.payload);
      setStatus({
        kind: "success",
        message: `Uitnodiging verstuurd naar ${result.payload.email}.`,
      });
      setEmail("");
      setDisplayName("");
      setRole(ROLE_OPTIONS[0]?.code ?? "");
    } catch (error) {
      setStatus({
        kind: "error",
        message: `Uitnodigen mislukt: ${error instanceof Error ? error.message : "Onbekende fout"}`,
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleRecovery() {
    setStatus(null);
    const result = buildRecoveryPayload({ email, project: project ?? undefined });
    if ("error" in result) {
      setStatus({ kind: "error", message: result.error });
      return;
    }

    setBusy(true);
    try {
      await adminApi.recoverInvitation(result.payload);
      setStatus({
        kind: "success",
        message:
          `Projecttoegang voor ${result.payload.email} is hersteld. Laat de gebruiker opnieuw inloggen.`,
      });
    } catch (error) {
      setStatus({
        kind: "error",
        message: `Herstellen mislukt: ${error instanceof Error ? error.message : "Onbekende fout"}`,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout wide>
      <h1>Gebruiker uitnodigen</h1>
      {allowed === null && (
        <section>
          <p>Laden...</p>
        </section>
      )}
      {allowed === false && (
        <section id="denied">
          <p>Je hebt geen rechten om gebruikers uit te nodigen voor dit project.</p>
          <div className="actions" style={{ justifyContent: "center" }}>
            <button
              type="button"
              className="secondary"
              onClick={() => navigate("/home/")}
            >
              Terug
            </button>
          </div>
        </section>
      )}
      {allowed && (
        <section>
          <p className="muted">
            Project: {project?.display_name ?? project?.project_code ?? PROJECT_CODE}
          </p>
          <form id="invite-form" onSubmit={handleInvite}>
            <div className="field">
              <label htmlFor="invite-email">E-mailadres</label>
              <input
                id="invite-email"
                type="email"
                autoComplete="off"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={busy}
              />
            </div>
            <div className="field">
              <label htmlFor="invite-name">Naam</label>
              <input
                id="invite-name"
                type="text"
                autoComplete="off"
                required
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                disabled={busy}
              />
            </div>
            <div className="field">
              <label htmlFor="invite-role">Rol</label>
              <select
                id="invite-role"
                value={role}
                onChange={(event) => setRole(event.target.value)}
                disabled={busy}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option value={option.code} key={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="actions">
              <button type="submit" disabled={busy}>
                {busy ? "Bezig..." : "Uitnodiging versturen"}
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => void handleRecovery()}
              >
                Bestaande toegang herstellen
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => navigate("/home/")}
              >
                Terug
              </button>
            </div>
          </form>
          <p className="muted">
            Gebruik “Bestaande toegang herstellen” alleen voor iemand die de eerdere uitnodiging al
            heeft bevestigd en ingelogd, maar het project niet ziet.
          </p>
          {status && (
            <section id="status" className={status.kind} role="status">
              {status.message}
            </section>
          )}
        </section>
      )}
    </AuthLayout>
  );
}
